import React, { useState, useEffect } from 'react';
import { Calendar, Play, RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface SyncLog {
  id: string;
  platform: string;
  accountId: string;
  status: 'running' | 'completed' | 'failed';
  startTime: string;
  endTime?: string;
  recordsProcessed: number;
  errors: string[];
  duration?: number;
}

const HistoricalSync: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [currentSync, setCurrentSync] = useState<SyncLog | null>(null);

  // Set default dates (30 days ago to today)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const platforms = [
    { id: 'all', name: 'All Platforms', description: 'Sync all payment platforms' },
    { id: 'stripe', name: 'Stripe', description: 'Sync all 5 Stripe accounts' },
    { id: 'authorizenet', name: 'Authorize.net', description: 'Sync all 5 Authorize.net accounts' },
    { id: 'shopify', name: 'Shopify', description: 'Sync Shopify store' },
  ];

  const startHistoricalSync = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setIsSyncing(true);
    const syncId = `sync_${Date.now()}`;
    
    const newSync: SyncLog = {
      id: syncId,
      platform: selectedPlatform,
      accountId: selectedPlatform === 'all' ? 'all' : selectedPlatform,
      status: 'running',
      startTime: new Date().toISOString(),
      recordsProcessed: 0,
      errors: [],
    };

    setCurrentSync(newSync);
    setSyncLogs(prev => [newSync, ...prev]);

    try {
      let endpoint = '';
      if (selectedPlatform === 'all') {
        endpoint = `${API_BASE}/api/sync/historical`;
      } else if (selectedPlatform === 'stripe') {
        endpoint = `${API_BASE}/api/sync/stripe/historical`;
      } else if (selectedPlatform === 'authorizenet') {
        endpoint = `${API_BASE}/api/sync/authorizenet/historical`;
      } else if (selectedPlatform === 'shopify') {
        endpoint = `${API_BASE}/api/sync/shopify/historical`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate,
          endDate,
          platform: selectedPlatform
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update sync log with results
        const updatedSync: SyncLog = {
          ...newSync,
          status: 'completed',
          endTime: new Date().toISOString(),
          recordsProcessed: result.totalRecordsProcessed || 0,
          duration: Date.now() - new Date(newSync.startTime).getTime(),
        };

        setCurrentSync(null);
        setSyncLogs(prev => prev.map(log => log.id === syncId ? updatedSync : log));
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      const failedSync: SyncLog = {
        ...newSync,
        status: 'failed',
        endTime: new Date().toISOString(),
        errors: [errorMessage],
        duration: Date.now() - new Date(newSync.startTime).getTime(),
      };

      setCurrentSync(null);
      setSyncLogs(prev => prev.map(log => log.id === syncId ? failedSync : log));
    } finally {
      setIsSyncing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'completed':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'failed':
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    return `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Historical Data Sync</h1>
        <p className="text-gray-600">Sync historical payment data from specific date ranges</p>
      </div>

      {/* Sync Configuration */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sync Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Platform Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform
            </label>
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="input"
              disabled={isSyncing}
            >
              {platforms.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {platforms.find(p => p.id === selectedPlatform)?.description}
            </p>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="input pl-10"
                disabled={isSyncing}
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="input pl-10"
                disabled={isSyncing}
              />
            </div>
          </div>
        </div>

        {/* Sync Button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={startHistoricalSync}
            disabled={isSyncing || !startDate || !endDate}
            className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {isSyncing ? 'Syncing...' : 'Start Historical Sync'}
          </button>

          {currentSync && (
            <div className="flex items-center space-x-2 text-sm text-blue-600">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Sync in progress...</span>
            </div>
          )}
        </div>

        {/* Date Range Info */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>Date Range:</strong> {startDate} to {endDate}
            {startDate && endDate && (
              <span className="ml-2">
                ({Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))} days)
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Sync Logs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Sync History</h3>
          <button
            onClick={() => setSyncLogs([])}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Logs
          </button>
        </div>

        {syncLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No sync operations yet</p>
            <p className="text-sm">Start a historical sync to see logs here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {syncLogs.map((log) => (
              <div
                key={log.id}
                className={`p-4 border rounded-lg ${getStatusColor(log.status)}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(log.status)}
                    <div>
                      <p className="font-medium">
                        {log.platform === 'all' ? 'All Platforms' : log.platform} Sync
                      </p>
                      <p className="text-sm opacity-75">
                        Started: {new Date(log.startTime).toLocaleString()}
                        {log.endTime && (
                          <span className="ml-2">
                            • Duration: {log.duration ? formatDuration(log.duration) : 'N/A'}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-medium">
                      {log.recordsProcessed} records
                    </p>
                    {log.errors.length > 0 && (
                      <p className="text-sm text-red-600">
                        {log.errors.length} error(s)
                      </p>
                    )}
                  </div>
                </div>

                {log.errors.length > 0 && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                    <p className="text-sm font-medium text-red-800 mb-2">Errors:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      {log.errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoricalSync;